<script setup lang="ts">

import {useShipActionStore} from "@/stores/shipActionStore.ts";
import Backend from '@/models/backend.ts'
import { useFleetStore } from '@/stores/fleetStore.ts'
import { useShipStore } from '@/stores/shipStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { useMapStore } from '@/stores/mapStore.ts'
import { CDN } from '@/models/cdn.ts'
import DeleteButton from '@/components/Elements/Buttons/DeleteButton.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { ref } from 'vue'
import { useRoute } from 'vue-router'

/**
 * Stores / models
 */
const shipActionStore = useShipActionStore()
const fleetStore = useFleetStore()
const shipStore = useShipStore()
const gameStore = useGameStore()
const mapStore = useMapStore()
const route = useRoute()

// Track if actions are being submitted
const isSubmitting = ref(false)

/**
 * Functions
 */
const resetAction = () => {
  shipActionStore.reset()
}

const undoLastAction = () => {
  shipActionStore.setPendingAction('')
  mapStore.shipActionPath.removeLastAction()
}

const setActions = () => {
  isSubmitting.value = true
  const backend = new Backend()
  if (shipActionStore.fleetId > 0) {
    //Store fleet orders
    backend.setFleetActions(shipActionStore.fleetId, shipActionStore.actions)
      .then(() => {
        gameStore.setTinySuccessToastMessage('Orders set for fleet "' + fleetStore.getFleetNameById(shipActionStore.fleetId) + '"')
        shipActionStore.reset()
      })
      .catch((error) => {
      gameStore.setTinyErrorToastMessage(error.response.data.message)
      shipActionStore.reset()
    })
      .finally(() => {
        isSubmitting.value = false
      })
  } else {
    //Store ship orders
    backend.setShipActions(shipActionStore.ships, shipActionStore.actions)
      .then(() => {
        gameStore.setTinySuccessToastMessage('Orders set for ' + shipActionStore.ships.length + ' ship(s)')
        shipActionStore.reset()
      })
      .catch((error) => {
      gameStore.setTinyErrorToastMessage(error.response.data.message)
      shipActionStore.reset()
    })
      .finally(() => {
        isSubmitting.value = false
      })
  }
}

const queueExplore = () => {
  shipActionStore.setPendingAction('explore')
  shipActionStore.queuePendingAction(
    0,
    0,
    0,
    0
  );
  setActions()
}

const queueRepair = () => {
  // Open repair action configuration modal
  gameStore.modalType = 'repairAction'
}

const queueDefend = () => {
  shipActionStore.setPendingAction('defend')
  
  // Use the position where the last command ended if there are previous commands,
  // otherwise use the current position
  const x = shipActionStore.actions.length > 0 ? shipActionStore.pathEndX : shipActionStore.pathStartX
  const y = shipActionStore.actions.length > 0 ? shipActionStore.pathEndY : shipActionStore.pathStartY
  
  shipActionStore.queuePendingAction(
    x,
    y,
    0,
    0
  );
  setActions()
}

const detachShipsFromFleet = () => {
  const ids = []; // Use a different variable name to avoid redeclaration confusion
  for (const ship of shipStore.ships.values()) {
    if (
      Number(ship.xPos) >= Number(mapStore.areaBoxTL.x) &&
      Number(ship.xPos) <= Number(mapStore.areaBoxRB.x) &&
      Number(ship.yPos) <= Number(mapStore.areaBoxTL.y) &&
      Number(ship.yPos) >= Number(mapStore.areaBoxRB.y)
    ) {
      if (ship.fleetId === shipActionStore.fleetId) {
        ids.push(ship.id);
      }
    }
  }

  const backend = new Backend()
  backend.detachShipsFromFleet(ids)

  shipActionStore.reset()
  gameStore.setTinySuccessToastMessage(ids.length + ' Ships detached from fleet')
}

/**
 * Event listeners
 */
window.addEventListener('keydown', (event) => {
  // Only handle keyboard shortcuts when on the map view
  if(!route.name?.toString().startsWith('MapGalaxy')) {
    return
  }

  if(shipActionStore.initialized) {
    if (event.key === 'm' || event.key === 'M') {
      shipActionStore.setPendingAction('move')
    } else if(event.key === 'a' || event.key === 'A') {
      shipActionStore.setPendingAction('attack')
    } else if(event.key === 's' || event.key === 'S') {
      shipActionStore.setPendingAction('settle')
    } else if (event.key === 'f' || event.key === 'F') {
      shipActionStore.setPendingAction('follow')
    } else if (event.key === 't' || event.key === 'T') {
      shipActionStore.setPendingAction('followAttack')
    } else if (event.key === 'r' || event.key === 'R') {
      queueRepair()
    } else if (event.key === 'e' || event.key === 'E') {
      queueExplore()
    } else if (event.key === 'l' || event.key === 'L') {
      detachShipsFromFleet()
    } else if (event.key === 'd' || event.key === 'D') {
      queueDefend()
    }
    //esc
    if (event.key === 'Escape') {
      shipActionStore.reset();
    }
    //del
    if (event.key === 'Delete' || event.key === 'Backspace') {
      shipActionStore.setPendingAction('')
      mapStore.shipActionPath.removeLastAction()
    }
  }
  if(mapStore.areaActionSelecting) {
    if (event.key === 'Escape') {
      mapStore.disableAreaSelection();
    }
  }
})

</script>

<template>
<div v-if="shipActionStore.initialized" class="absolute w-full top-0 text-center flex justify-center pointer-events-none">
  <div class="flex flex-col bg-slate-900 p-2 pointer-events-auto rounded-b text-slate-300">
    <div v-if="shipActionStore.fleetId === 0">{{ shipActionStore.ships.length }} ship(s) selected</div>
    <div v-else>Fleet "{{ fleetStore.getFleetNameById(Number(shipActionStore.fleetId)) }}" selected</div>
    <div class="grid grid-cols-8">
      <div :class="{
              'cursor-pointer p-1 m-1 rounded' : true,
              'bg-slate-600' : shipActionStore.pendingAction === 'move',
              'bg-slate-800 hover:bg-slate-600 ' : shipActionStore.pendingAction !== 'move'
           }"
           @click="shipActionStore.setPendingAction('move')">
        <img :src="CDN.icon32('cmd_move')" alt="Move (m)" title="Move (m)" class="w-8 h-8 p-1"/>
      </div>
      <div :class="{
              'cursor-pointer p-1 m-1 rounded' : true,
              'bg-slate-600' : shipActionStore.pendingAction === 'attack',
              'bg-slate-800 hover:bg-slate-600 ' : shipActionStore.pendingAction !== 'attack'
           }"
           @click="shipActionStore.setPendingAction('attack')">
        <img :src="CDN.icon32('cmd_attack')" alt="Attack (a)" title="Attack (a)" class="w-8 h-8 p-1"/>
      </div>
      <div :class="{
              'cursor-pointer p-1 m-1 rounded' : true,
              'bg-slate-600' : shipActionStore.pendingAction === 'settle',
              'bg-slate-800 hover:bg-slate-600 ' : shipActionStore.pendingAction !== 'settle'
           }"
             @click="shipActionStore.setPendingAction('settle')">
          <img :src="CDN.icon32('cmd_settle')" alt="Settle (s)" title="Settle (s)" class="w-8 h-8 p-1"/>
      </div>
      <div :class="{
              'cursor-pointer p-1 m-1 rounded' : true,
              'bg-slate-600' : shipActionStore.pendingAction === 'follow',
              'bg-slate-800 hover:bg-slate-600 ' : shipActionStore.pendingAction !== 'follow'
           }"
           @click="shipActionStore.setPendingAction('follow')">
        <img :src="CDN.icon32('cmd_follow')" alt="Follow (f)" title="Follow (f)" class="w-8 h-8 p-1"/>
      </div>
      <div :class="{
              'cursor-pointer p-1 m-1 rounded' : true,
              'bg-slate-600' : shipActionStore.pendingAction === 'followAttack',
              'bg-slate-800 hover:bg-slate-600 ' : shipActionStore.pendingAction !== 'followAttack'
           }"
           @click="shipActionStore.setPendingAction('followAttack')">
        <img :src="CDN.icon32('cmd_followattack')" alt="Follow & Attack (t)" title="Follow & Attack (t)" class="w-8 h-8 p-1"/>
      </div>
      <div v-if="shipActionStore.fleetId === 0" :class="{
              'cursor-pointer p-1 m-1 rounded' : true,
              'bg-slate-600' : shipActionStore.pendingAction === 'explore',
              'bg-slate-800 hover:bg-slate-600 ' : shipActionStore.pendingAction !== 'explore'
           }"
           @click="queueExplore()">
        <img :src="CDN.icon32('cmd_search')" alt="Explore (e)" title="Explore (e)" class="w-8 h-8 p-1"/>
      </div>
      <div :class="{
              'cursor-pointer p-1 m-1 rounded' : true,
              'bg-slate-600' : shipActionStore.pendingAction === 'repair',
              'bg-slate-800 hover:bg-slate-600 ' : shipActionStore.pendingAction !== 'repair'
           }"
           @click="queueRepair()">
        <img :src="CDN.icon32('cmd_repair')" alt="Repair (r)" title="Repair (R)" class="w-8 h-8 p-1"/>
      </div>
      <div :class="{
              'cursor-pointer p-1 m-1 rounded' : true,
              'bg-slate-600' : shipActionStore.pendingAction === 'defend',
              'bg-slate-800 hover:bg-slate-600 ' : shipActionStore.pendingAction !== 'defend'
           }"
           @click="queueDefend()">
        <img :src="CDN.icon32('shields')" alt="Defend (d)" title="Defend (d)" class="w-8 h-8 p-1"/>
      </div>
      <div v-if="shipActionStore.fleetId > 0" :class="{
              'cursor-pointer p-1 m-1 rounded bg-slate-800 hover:bg-slate-600' : true,
           }"
           @click="detachShipsFromFleet()">
        <img :src="CDN.icon32('cmd_decouple')" alt="Detach selected ships (l)" title="Detach selected ships (l)" class="w-8 h-8 p-1"/>
      </div>
    </div>
    <div v-if="!isSubmitting" class="pt-2 flex justify-evenly gap-2">
      <ActionButton v-if="shipActionStore.actions.length>0" @click="setActions">Set</ActionButton>
      <ActionButton v-if="shipActionStore.actions.length>0" @click="undoLastAction">Undo</ActionButton>
      <DeleteButton  @click="resetAction">Cancel</DeleteButton>
    </div>
  </div>
</div>
</template>

<style scoped lang="scss">

</style>